/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import {
    Heading,
    TextField,
    Button,
    PasswordRequirements,
    passwordValidator,
    type FormValidationAlertErrors,
    FormValidationAlert,
    useFlags,
} from 'sarsaparilla';
import { useFieldRefValidation } from 'sarsaparilla/src/utils';

import { useRecaptchaWithFallback } from 'shared-ui/recaptcha';
import {
    getEmailConfirmedToken,
    postLoginV2,
    postSetPassword,
} from 'shared-ui/api/account';

import { matchPasswordValidator } from 'shared-ui/utils/validators';
import { useAuthModalScreen } from '../AuthModalScreenContext';

import {
    SetPasswordFormErrors,
    type SetPasswordFormErrorType,
} from './SetPasswordFormErrors';

interface SetPasswordFormProps {
    /**
     * Called when the password was saved but the courtesy auto-login could not
     * complete (e.g. reCAPTCHA wanted an extra challenge). The user is sent to a
     * success screen and from there to the login screen, which owns the full
     * reCAPTCHA v2 fallback and login error handling.
     */
    transitionToSuccessScreen: () => void;
}

export function SetPasswordForm({ transitionToSuccessScreen }: SetPasswordFormProps) {
    const { accountLoginRecaptcha } = useFlags();
    const isRecaptchaEnabled = accountLoginRecaptcha === true;

    const { storeUserData, cleanUpParamsAndStorage, closeModal } = useAuthModalScreen();
    const [isLoading, setIsLoading] = React.useState(false);
    const [password, setPassword] = React.useState('');
    const [errorType, setErrorType] = React.useState<SetPasswordFormErrorType>(null);
    const [validationErrors, setValidationErrors] =
        React.useState<FormValidationAlertErrors | null>(null);

    const { getToken, getRecaptchaRegion } = useRecaptchaWithFallback();

    const { addFieldRef, validate } = useFieldRefValidation();

    const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setIsLoading(true);
        setErrorType(null);
        setValidationErrors(null);

        const formData = new FormData(event.currentTarget);
        const passwordRepeat = formData.get('passwordRepeat')?.toString().trim();

        const { isValid, errors } = validate();

        if (!isValid || !password || !passwordRepeat) {
            setValidationErrors(errors);
            setIsLoading(false);
            return;
        }

        const token = getEmailConfirmedToken();

        if (!token) {
            setErrorType('unknown');
            setIsLoading(false);
            return;
        }

        // Step 1: Save the password. This is the only step that can surface an
        // inline error on this screen - once it succeeds the account exists.
        let email: string;

        try {
            const { data } = await postSetPassword({ token, password });
            email = data.account.email;
        } catch (error) {
            setErrorType('unknown');
            setIsLoading(false);
            return;
        }

        // Step 2: Attempt courtesy auto-login. The password is already saved, so any failure
        // (including a reCAPTCHA challenge) hands off to the success screen -> login
        // screen rather than showing an error unrelated to saving the password here.
        try {
            const recaptchaToken = isRecaptchaEnabled
                ? await getToken('login')
                : undefined;
            const recaptchaType = isRecaptchaEnabled ? getRecaptchaRegion() : undefined;

            if (isRecaptchaEnabled && !recaptchaToken) {
                transitionToSuccessScreen();
                return;
            }

            const { data: loginData } = await postLoginV2({
                username: email,
                password,
                token,
                recaptchaToken,
                recaptchaType,
            });

            // Store user data to make login "permanent"
            storeUserData(loginData);

            // Clean up params and temporary storage
            cleanUpParamsAndStorage();

            closeModal();
        } catch (error) {
            transitionToSuccessScreen();
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="p-sm-3">
            <Heading headingLevel={1} appearance="h4" className="mb-1" id="modal-heading">
                Set Your Password
            </Heading>
            <div className="pt-half text-md">
                Create a secure password for your new account.
            </div>

            <form onSubmit={onSubmit} className="mt-5">
                <FormValidationAlert errors={validationErrors} className="mb-4" />

                <SetPasswordFormErrors errorType={errorType} className="mb-4" />

                <TextField
                    label="New Password"
                    type="password"
                    id="password"
                    name="password"
                    value={password}
                    onChange={(event) => setPassword(event.currentTarget.value)}
                    validator={passwordValidator}
                    isRequired
                    enableShowPassword
                    ref={addFieldRef(0)}
                />

                <PasswordRequirements password={password} className="mt-1" />

                <TextField
                    label="Re-Enter New Password"
                    type="password"
                    id="passwordRepeat"
                    name="passwordRepeat"
                    className="mt-4"
                    isRequired
                    enableShowPassword
                    validator={matchPasswordValidator(password)}
                    ref={addFieldRef(1)}
                />

                <Button
                    type="submit"
                    size="lg"
                    appearance="primary"
                    shouldFitContainer
                    className="mt-5"
                    isLoading={isLoading}
                >
                    Save Password
                </Button>
            </form>
        </div>
    );
}
